// Daily lesson selection — ported from docs/DEMO_REFERENCE.html `pickTodaysLesson`.
//
// Selection order (first match wins):
//   1. Manual override (set by the lesson preferences popup — passed in by caller)
//   2. Highest-weight matching schedule entry for today's date
//   3. Goal-driven default (GOAL_TO_LESSON[firstGoal].today)
//   4. TODAY_DEFAULT
//
// Match conditions on each entry's `when`:
//   dayOfWeek: 0 (Sun) – 6 (Sat)
//   dayOfMonth: 1–31
//   month: 1–12
//   dayRange: [start, end] — inclusive, requires `month`
//
// Multiple conditions on one entry = AND. Add separate entries for OR.

export const TODAY_DEFAULT = 'weather-contingency';

/**
 * Maps a goal id (kebab-case, as stored in users.goals) to the lesson category
 * (Title Case, as stored in lessons.category) it most aligns with. Used by the
 * bonus-pick flow to pull a goal-aligned un-delivered lesson when the barber
 * taps "Read another."
 */
export const GOAL_TO_CATEGORY: Record<string, string> = {
  'retention':       'Retention',
  'pricing':         'Pricing',
  'marketing':       'Marketing',
  'new-clients':     'Marketing',
  'time-management': 'Time management',
  'skill':           'Skill',
  'customer-service':'Customer service',
};

export const GOAL_TO_LESSON: Record<string, { today: string; next: string[] }> = {
  'retention':       { today: 'tue-recovery',     next: ['rebook-script', 'reading-room', 'consult-30s'] },
  'new-clients':     { today: 'before-after-post',next: ['rebook-script', 'reading-room', 'consult-30s'] },
  'pricing':         { today: 'beard-pricing',    next: ['holiday-pricing', 'consult-30s', 'rebook-script'] },
  'time-management': { today: 'buffer-noshow',    next: ['rebook-script', 'consult-30s', 'reading-room'] },
  'marketing':       { today: 'before-after-post',next: ['rebook-script', 'consult-30s', 'reading-room'] },
  'skill':           { today: 'consult-30s',      next: ['burst-fade', 'reading-room', 'rebook-script'] },
};

type ScheduleWhen = {
  dayOfWeek?: number;
  dayOfMonth?: number;
  month?: number;
  dayRange?: [number, number];
};

type ScheduleEntry = {
  lessonId: string;
  weight: number;
  when: ScheduleWhen;
};

export const LESSON_SCHEDULE: readonly ScheduleEntry[] = [
  // Tuesday: classic "afternoon recovery"
  { lessonId: 'tue-recovery',         weight: 9,  when: { dayOfWeek: 2 } },
  // First of every month: annual price increase context
  { lessonId: 'annual-increase',      weight: 8,  when: { dayOfMonth: 1 } },
  // Friday: Google reviews
  { lessonId: 'google-reviews',       weight: 7,  when: { dayOfWeek: 5 } },
  // Saturday: first hour rituals
  { lessonId: 'first-hour',           weight: 7,  when: { dayOfWeek: 6 } },
  // Sunday: seasonal campaign timing
  { lessonId: 'seasonal-campaign',    weight: 6,  when: { dayOfWeek: 0 } },
  // Holiday season pricing — Dec 18-24
  { lessonId: 'holiday-pricing',      weight: 11, when: { month: 12, dayRange: [18, 24] } },
  // Valentine's week — premium experience pitch
  { lessonId: 'premium-tier',         weight: 10, when: { month: 2,  dayRange: [10, 14] } },
  // Mother's Day weekend (approx) — quarterly thank-you energy
  { lessonId: 'quarterly-thanks',     weight: 10, when: { month: 5,  dayRange: [10, 14] } },
  // Back-to-school window — first-cut text for new students
  { lessonId: 'first-cut-text',       weight: 10, when: { month: 8,  dayRange: [15, 31] } },
  // New Year — annual price increase reminder again with extra weight
  { lessonId: 'annual-increase',      weight: 11, when: { month: 1,  dayRange: [1, 7] } },
  // Mid-March — start of spring cleanup vibes; clipper maintenance
  { lessonId: 'clipper-maintenance',  weight: 8,  when: { month: 3,  dayRange: [15, 21] } },
  // Father's Day weekend (mid-June) — bring-a-friend day energy
  { lessonId: 'bring-friend-day',     weight: 9,  when: { month: 6,  dayRange: [14, 21] } },
  // Halloween week — TikTok cut-along (Oct 25-31)
  { lessonId: 'tiktok-cutalong',      weight: 8,  when: { month: 10, dayRange: [25, 31] } },
  // Thanksgiving week (Nov 22-28 covers most years) — quarterly thank-you notes
  { lessonId: 'quarterly-thanks',     weight: 9,  when: { month: 11, dayRange: [22, 28] } },
];

/**
 * Plain-language description of when a lesson will be delivered. Used by the
 * Lessons tab to surface why a locked tile is locked. Returns a key+vars pair
 * the consumer resolves via t(). Returns null for the catalog's fallback
 * lesson (TODAY_DEFAULT) since it has no schedule and shouldn't render a
 * misleading "any day" hint.
 */
export type DeliveryHint = { key: string; vars?: Record<string, string | number> };

export function deliveryHint(
  lessonId: string,
  goals?: readonly string[],
): DeliveryHint {
  // Calendar schedule — preferred since it's most specific.
  for (const e of LESSON_SCHEDULE) {
    if (e.lessonId !== lessonId) continue;
    if (e.when.dayOfWeek != null) {
      return { key: 'lessons.hint.dayOfWeek', vars: { dow: e.when.dayOfWeek } };
    }
    if (e.when.month != null && e.when.dayRange) {
      return {
        key: 'lessons.hint.dateRange',
        vars: { month: e.when.month, start: e.when.dayRange[0], end: e.when.dayRange[1] },
      };
    }
    if (e.when.month != null) {
      return { key: 'lessons.hint.month', vars: { month: e.when.month } };
    }
    if (e.when.dayOfMonth != null) {
      return { key: 'lessons.hint.dayOfMonth', vars: { n: e.when.dayOfMonth } };
    }
  }
  // Goal-aligned fallback path.
  if (goals && goals.length > 0) {
    for (const g of goals) {
      const slot = GOAL_TO_LESSON[g];
      if (!slot) continue;
      if (slot.today === lessonId) return { key: 'lessons.hint.goalToday', vars: { goal: g } };
      if (slot.next.includes(lessonId)) return { key: 'lessons.hint.goalNext', vars: { goal: g } };
    }
  }
  // Universal fallback.
  if (lessonId === TODAY_DEFAULT) return { key: 'lessons.hint.fallback' };
  return { key: 'lessons.hint.unscheduled' };
}

function scheduleMatches(when: ScheduleWhen, dow: number, dom: number, month: number): boolean {
  if (when.dayOfWeek != null && when.dayOfWeek !== dow) return false;
  if (when.dayOfMonth != null && when.dayOfMonth !== dom) return false;
  if (when.month != null && when.month !== month) return false;
  if (when.dayRange) {
    const [start, end] = when.dayRange;
    if (dom < start || dom > end) return false;
  }
  return true;
}

/**
 * Picks today's lesson ID. Caller passes the user's goals (optional) and
 * a manual override (optional, takes precedence).
 *
 * If `deliveredIds` is provided, lessons in that set are deprioritized: the
 * picker prefers a fresh lesson over re-serving one the barber has already
 * read, even if the schedule would naturally repeat. Useful for the
 * skip-ahead flow ("tomorrow's lesson" should never be one they already
 * pulled) and for the daily Today screen on subsequent calendar days
 * after a skip ahead landed yesterday's pick early. The filter is
 * preferred-not-strict: when the entire pool of fresh candidates is
 * empty, falls back to the natural pick so something always renders.
 *
 * Falls through to TODAY_DEFAULT if no schedule entry or goal matches.
 */
export function pickTodaysLessonId(
  opts: {
    goals?: readonly string[];
    override?: string | null;
    /** Inject a date for testing. Defaults to now(). */
    now?: Date;
    /** Set of valid lesson IDs from the DB — schedule picks are filtered to ones that exist. */
    validIds?: ReadonlySet<string>;
    /** Lessons the barber has been served in the last 90 days. HARD
     *  SKIP set — the picker will not return any ID in here unless
     *  every other path is exhausted. This is what guarantees a 90-day
     *  rotation: a barber never sees the same lesson twice within a
     *  rolling three-month window. */
    recentDeliveredIds?: ReadonlySet<string>;
    /** Full lesson catalog (id + category). Drives the category-
     *  preference fallback when the scheduled entry and the goal
     *  anchor are both on cooldown. The picker scans the catalog for
     *  any fresh lesson tagged with the barber's preferred category
     *  before giving up and falling through to TODAY_DEFAULT.
     *  Optional — picker degrades gracefully when missing. */
    catalog?: ReadonlyArray<{ id: string; category: string }>;
  } = {},
): string {
  if (opts.override) return opts.override;
  const d = opts.now ?? new Date();
  const dow = d.getDay();
  const dom = d.getDate();
  const month = d.getMonth() + 1;

  const recent = opts.recentDeliveredIds ?? new Set<string>();
  const exists = (id: string) => !opts.validIds || opts.validIds.has(id);
  const fresh = (id: string) => exists(id) && !recent.has(id);

  // 1. Schedule — highest weight wins, MUST be fresh (not delivered in
  //    the last 90 days). If today's scheduled entries are all on
  //    cooldown, we fall through to the goal / category path rather
  //    than re-serving them.
  const scheduledForToday = LESSON_SCHEDULE
    .filter((e) => scheduleMatches(e.when, dow, dom, month))
    .slice()
    .sort((a, b) => b.weight - a.weight);
  for (const m of scheduledForToday) {
    if (fresh(m.lessonId)) return m.lessonId;
  }

  // 2. Goal anchor — try the goal's "today" + "next" array, fresh only.
  //    Honors the barber's stated focus by picking the lesson the goal
  //    map specifically aligns with their goal.
  if (opts.goals && opts.goals.length > 0) {
    const slot = GOAL_TO_LESSON[opts.goals[0]];
    if (slot) {
      if (fresh(slot.today)) return slot.today;
      for (const id of slot.next) {
        if (fresh(id)) return id;
      }
    }
  }

  // 3. Category preference — any fresh lesson tagged with one of the
  //    barber's goal categories. Iterates goals in priority order, so
  //    the primary goal wins. The catalog scan is what makes the
  //    "rotation with category preference" promise concrete: after the
  //    schedule and the goal anchor are both on cooldown, the picker
  //    still serves something tagged with the barber's focus topic
  //    before falling through to a generic default.
  if (opts.goals && opts.goals.length > 0 && opts.catalog) {
    for (const goal of opts.goals) {
      const cat = GOAL_TO_CATEGORY[goal];
      if (!cat) continue;
      const inCategory = opts.catalog.filter((l) => l.category === cat && fresh(l.id));
      if (inCategory.length > 0) {
        return inCategory[deterministicIndex(d, inCategory.length)].id;
      }
    }
  }

  // 4. TODAY_DEFAULT — only if it's fresh.
  if (fresh(TODAY_DEFAULT)) return TODAY_DEFAULT;

  // 5. Any fresh lesson catalog-wide. Date-anchored so the same day
  //    always picks the same lesson (avoids flicker on refresh).
  if (opts.catalog) {
    const anyFresh = opts.catalog.filter((l) => fresh(l.id));
    if (anyFresh.length > 0) {
      return anyFresh[deterministicIndex(d, anyFresh.length)].id;
    }
  }

  // 6. Catalog exhausted — every lesson has been delivered within the
  //    rolling 90-day window. Rare: requires < 90 lessons total. Fall
  //    back to the schedule (even if delivered), then to the goal
  //    anchor, then to TODAY_DEFAULT so the UI always renders.
  for (const m of scheduledForToday) {
    if (exists(m.lessonId)) return m.lessonId;
  }
  if (opts.goals && opts.goals.length > 0) {
    const slot = GOAL_TO_LESSON[opts.goals[0]];
    if (slot && exists(slot.today)) return slot.today;
  }
  return TODAY_DEFAULT;
}

/** Date-anchored hash so a multi-element pool always picks the same
 *  element on the same day — prevents the lesson changing on refresh
 *  during the day. Simple integer hash of YYYYMMDD; good enough for
 *  small mod values (we're modding by category sizes ~16). */
function deterministicIndex(d: Date, mod: number): number {
  if (mod <= 1) return 0;
  const key = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
  let h = key >>> 0;
  h = ((h ^ (h >>> 16)) * 2246822507) >>> 0;
  h = ((h ^ (h >>> 13)) * 3266489909) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  return h % mod;
}
